import { Injectable, signal } from '@angular/core';
import { LoanSummary } from '../../../core/credit-calculator/credit-calculator.interface';

/** Respuesta parcial de GET personas/buscar-persona (core / middleware). */
export interface BuscarPersonaUserData {
  firstName?: string;
  lastName?: string;
  floor?: string;
  letter?: string;
  streetNumber?: string;
  street?: string;
  provincia?: string;
  ciudad?: string;
  /** Core a veces devuelve esta clave (typo histórico). */
  bithDate?: string;
  birthDate?: string;
}

export interface StartData {
  dni: string;
  genero: string;
  email: string;
  countryCode: string;
  phone: string;
  /** CUIT 11 dígitos usado en buscar-persona y registro. */
  cuit: string;
  /** Última respuesta de análisis (billetera-core vía middleware). */
  buscarPersona?: {
    aprovado: boolean;
    offer?: unknown;
    error?: unknown;
    extra?: unknown;
    userData?: BuscarPersonaUserData;
  };
}

export interface PersonalData {
  nombre: string;
  primerApellido: string;
  segundoApellido?: string;
  fechaNacimiento: string;
  estadoCivil: string;
  calle: string;
  numero: string;
  piso?: string;
  letra?: string;
  ciudad: string;
  provincia: string;
  codigoPostal: string;
  /** IDs de catálogo core (formularios/motivos-prestamos, formularios/empleos). */
  motivoPrestamoId: number;
  empleoId: number;
  /** Etiquetas para UI (mismo criterio que widget). */
  motivoPrestamo: string;
  ocupacion: string;
  aceptaTerminos: boolean;
  noSujetoObligado: boolean;
  noPep: boolean;
}

/** Persisted session UUID (aligned with WidgetSessionService) */
export const WIDGET_SESSION_STORAGE_KEY = 'billetera.widget.sessionId';

/** UUID del SMS de verificación (recarga WebView / pérdida de `history.state`) */
export const WIDGET_VERIFICATION_UUID_KEY = 'billetera.widget.verificationUuid';

/** Datos obligatorios recibidos por query en /home (precarga solicitud). */
export interface HomeEntryContext {
  dni: string;
  genero: string;
  email: string;
  countryCode: string;
  phone: string;
  tenant: string;
}

@Injectable({ providedIn: 'root' })
export class CreditRequestStateService {
  loanSummary  = signal<LoanSummary | null>(null);
  startData    = signal<StartData | null>(null);
  personalData = signal<PersonalData | null>(null);
  private accessTokenSignal = signal<string | null>(null);
  private sessionIdSignal = signal<string | null>(null);
  private tenantAppTokenSignal = signal<string | null>(null);
  private homeEntryContextSignal = signal<HomeEntryContext | null>(null);

  /**
   * Snapshot de los query params de /home. Fuente de verdad durante todo el
   * flujo: los campos que llegaron por URL nunca se reemplazan con datos del
   * backend ni del formulario. Solo se limpia en reset() o al re-entrar por URL.
   */
  private urlEntryDataSignal = signal<HomeEntryContext | null>(null);
  urlEntryData = this.urlEntryDataSignal.asReadonly();

  /**
   * Tras checkDni/existe-cliente (como widget), antes de oferta o verify/personal-data.
   */
  private existeClienteSignal = signal(false);
  existeCliente = this.existeClienteSignal.asReadonly();

  /** Query de /home para volver desde oferta aceptada (WebView / app). */
  private homeCreditQueryParamsSignal = signal<Record<string, string> | null>(
    null,
  );
  homeCreditQueryParams = this.homeCreditQueryParamsSignal.asReadonly();

  /** Monto sugerido al aceptar oferta; home lo aplica al slider una vez. */
  private pendingSimulatorAmountSignal = signal<number | null>(null);

  /** UUID del último código SMS (sobrevive recarga; `history.state` no). */
  private verificationCodeUuidSignal = signal<string | null>(null);

  accessToken = this.accessTokenSignal.asReadonly();
  /** Anonymous session UUID (billetera-core SessionData), sent as X-User-Session */
  sessionId = this.sessionIdSignal.asReadonly();
  /** Token de aplicación / tenant desde URL; enviado como X-App-Token al middleware. */
  tenantAppToken = this.tenantAppTokenSignal.asReadonly();
  /** Contexto de entrada desde /home; se consume al precargar el paso inicial. */
  homeEntryContext = this.homeEntryContextSignal.asReadonly();

  setAccessToken(token: string): void {
    this.accessTokenSignal.set(token);
  }

  setSessionId(id: string): void {
    this.sessionIdSignal.set(id);
  }

  setTenantAppToken(token: string | null): void {
    const t = token?.trim();
    this.tenantAppTokenSignal.set(t && t.length > 0 ? t : null);
  }

  setHomeEntryContext(ctx: HomeEntryContext | null): void {
    this.homeEntryContextSignal.set(ctx);
  }

  setUrlEntryData(ctx: HomeEntryContext | null): void {
    this.urlEntryDataSignal.set(ctx);
  }

  /**
   * Setea startData garantizando que los campos que llegaron por URL
   * (dni, genero, email, celular) prevalezcan sobre cualquier otro valor.
   */
  setStartData(data: StartData | null): void {
    if (!data) {
      this.startData.set(null);
      return;
    }
    const url = this.urlEntryDataSignal();
    if (!url) {
      this.startData.set(data);
      return;
    }
    this.startData.set({
      ...data,
      dni: url.dni || data.dni,
      genero: url.genero || data.genero,
      email: url.email || data.email,
      ...(url.phone
        ? { countryCode: url.countryCode, phone: url.phone }
        : {}),
    });
  }

  setExisteCliente(value: boolean): void {
    this.existeClienteSignal.set(value);
  }

  setHomeCreditQueryParams(params: Record<string, string> | null): void {
    this.homeCreditQueryParamsSignal.set(params);
  }

  setPendingSimulatorAmount(amount: number | null): void {
    this.pendingSimulatorAmountSignal.set(amount);
  }

  /** Lee y limpia el monto pendiente (p. ej. tras aceptar oferta). */
  consumePendingSimulatorAmount(): number | null {
    const v = this.pendingSimulatorAmountSignal();
    this.pendingSimulatorAmountSignal.set(null);
    return v;
  }

  verificationCodeUuid = this.verificationCodeUuidSignal.asReadonly();

  setVerificationCodeUuid(uuid: string | null): void {
    const t = uuid?.trim();
    if (t && t.length > 0) {
      this.verificationCodeUuidSignal.set(t);
      try {
        sessionStorage.setItem(WIDGET_VERIFICATION_UUID_KEY, t);
      } catch {
        /* ignore */
      }
    } else {
      this.verificationCodeUuidSignal.set(null);
      try {
        sessionStorage.removeItem(WIDGET_VERIFICATION_UUID_KEY);
      } catch {
        /* ignore */
      }
    }
  }

  reset(): void {
    this.loanSummary.set(null);
    this.startData.set(null);
    this.personalData.set(null);
    this.accessTokenSignal.set(null);
    this.sessionIdSignal.set(null);
    this.tenantAppTokenSignal.set(null);
    this.homeEntryContextSignal.set(null);
    this.urlEntryDataSignal.set(null);
    this.existeClienteSignal.set(false);
    this.homeCreditQueryParamsSignal.set(null);
    this.pendingSimulatorAmountSignal.set(null);
    this.verificationCodeUuidSignal.set(null);
    sessionStorage.removeItem(WIDGET_SESSION_STORAGE_KEY);
    try {
      sessionStorage.removeItem(WIDGET_VERIFICATION_UUID_KEY);
    } catch {
      /* ignore */
    }
  }
}
